import React from "react";
import { ChevronRight, type LucideIcon } from "lucide-react";

interface EraCardProps {
  title: string;
  subtitle: string;
  description: string;
  image: string;
  icon: LucideIcon;
  iconColor: string;
  available: boolean;
  showNewBadge: boolean;
  wide?: boolean;
  imagePosition?: string;
  onClick: () => void;
  animationDelay: string;
}

const EraCard: React.FC<EraCardProps> = ({
  title,
  subtitle,
  description,
  image,
  icon: Icon,
  iconColor,
  available,
  showNewBadge,
  wide,
  imagePosition,
  onClick,
  animationDelay,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!available}
      className={`animate-slide-fade-in group relative overflow-hidden rounded-xl border border-border text-left transition-all duration-300 hover:border-muted-foreground hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/30 ${
        wide ? "md:col-span-2" : ""
      } ${!available ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      style={{ animationDelay, animationFillMode: "backwards" }}
    >
      {/* Single unified media layer — image + gradient transform together */}
      <div className="relative h-48 overflow-hidden">
        <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-110">
          <img
            src={image}
            alt={title}
            className={`absolute inset-0 w-full h-full object-cover grayscale transition-[filter] duration-500 group-hover:grayscale-0 ${
              imagePosition || (wide ? "object-[center_42%]" : "object-center")
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent pointer-events-none" />
        </div>
      </div>

      {/* Content — no negative margin, bg-card ensures seamless join */}
      <div className="relative z-10 bg-card p-6 flex items-end justify-between">
        <div>
          <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center mb-3">
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1 font-antiqua">
            {subtitle}
          </p>
          <h3 className="text-2xl font-bold text-white mb-2 font-antiqua">
            {title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground/50 group-hover:text-white transition-colors flex-shrink-0 ml-4 mb-1" />
      </div>

      {/* Badges */}
      {!available && (
        <span className="absolute top-4 right-4 text-xs uppercase tracking-wider text-muted-foreground/70 border border-muted-foreground/50 rounded px-2 py-1">
          Coming Soon
        </span>
      )}
      {showNewBadge && (
        <span className="absolute top-3 right-3 z-20 bg-card text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-0.5 transition-colors duration-300 group-hover:bg-emerald-600">
          New
        </span>
      )}
    </button>
  );
};

export default EraCard;
